/**
 * Public, read-only client preview page (no auth, outside the password gate).
 * Route: /preview/:token — renders a shareable batch of articles with a
 * "Preview" banner at the top. View-only: no editing, no comments.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import contentStudioService from '../../services/contentStudioService';
import { bodyToHtml, PROSE_CLASS } from '../content-studio/shared';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return ''; }
};

// Per-article "Request edits" — public comments. Comment text is rendered as
// plain text ({c.body}), so React escapes it — no stored-XSS risk.
const RequestEdits = ({ token, articleId }) => {
  const [comments, setComments] = useState([]);
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [hp, setHp] = useState('');           // honeypot — bots fill it, humans never see it
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const [justSent, setJustSent] = useState(false);

  useEffect(() => {
    let alive = true;
    contentStudioService.listPreviewComments(token, articleId)
      .then((d) => { if (alive) setComments(d?.comments || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [token, articleId]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!name.trim() || !body.trim()) { setErr('Please add your name and a comment.'); return; }
    setSending(true);
    try {
      const created = await contentStudioService.addPreviewComment(token, {
        article_id: articleId, author_name: name.trim(), body: body.trim(), hp,
      });
      if (created?.id) setComments((c) => [...c, created]);
      setBody(''); setJustSent(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Could not send — please try again.');
    } finally { setSending(false); }
  };

  return (
    <div className="border-t border-slate-100 bg-slate-50/60 px-7 sm:px-10 py-7" data-testid="preview-request-edits">
      <h3 className="text-sm font-semibold text-slate-900 mb-1">Request edits</h3>
      <p className="text-xs text-slate-500 mb-4">Leave a note for the team about anything you’d like changed in this article.</p>

      {comments.length > 0 && (
        <ul className="space-y-3 mb-5">
          {comments.map((c) => (
            <li key={c.id} className="bg-white border border-slate-200 rounded-lg px-4 py-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm font-semibold text-slate-800">{c.author_name}</span>
                <span className="text-[11px] text-slate-400">{fmtDate(c.created_at)}</span>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{c.body}</p>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="space-y-3">
        <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
        <input
          type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
          placeholder="Your name" data-testid="preview-comment-name"
          className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-kc-blue/30" />
        <textarea
          value={body} onChange={(e) => { setBody(e.target.value); setJustSent(false); }} rows={3} maxLength={5000}
          placeholder="What would you like changed?" data-testid="preview-comment-body"
          className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-kc-blue/30" />
        {err && <p className="text-xs text-red-600">{err}</p>}
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-emerald-600">{justSent ? 'Sent — thank you!' : ''}</span>
          <button type="submit" disabled={sending} data-testid="preview-comment-submit"
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-kc-blue text-white hover:bg-kc-blue-dk disabled:opacity-50">
            {sending ? 'Sending…' : 'Request edits'}
          </button>
        </div>
      </form>
    </div>
  );
};

const PreviewPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    contentStudioService.getPreview(token)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Loading preview…</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Preview not found</h1>
          <p className="text-slate-500 text-sm">This link may have expired or is no longer available.</p>
        </div>
      </div>
    );
  }

  const articles = data.articles || [];
  const clientName = articles[0]?.client_name || '';

  return (
    <div className="min-h-screen bg-slate-100" data-testid="public-preview-page">
      {/* Preview banner */}
      <div className="sticky top-0 z-20 bg-slate-900/95 backdrop-blur text-white border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest bg-white/15 px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              Preview
            </span>
            <span className="text-sm text-white/70 hidden sm:inline">Read-only client preview</span>
          </div>
          <span className="text-xs text-white/50">{articles.length} {articles.length === 1 ? 'article' : 'articles'}</span>
        </div>
      </div>

      {/* Cover header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 pt-12 pb-14 text-center">
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber-400 font-semibold mb-4">KeyCrew Content Studio</div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            {data.title || (clientName ? `${clientName} — article previews` : 'Article previews')}
          </h1>
          {clientName && (
            <p className="text-slate-300 mt-3 text-sm">
              Prepared for <span className="font-medium text-white">{clientName}</span>
              {data.created_at && <> · {fmtDate(data.created_at)}</>}
            </p>
          )}
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {articles.length === 0 ? (
          <p className="text-slate-500 text-sm">No articles in this preview.</p>
        ) : (
          <div className="space-y-10">
            {articles.map((a) => {
              const imageUrl = a.featured_image?.url ? `${BACKEND}${a.featured_image.url}` : null;
              const dests = (a.destinations || []).filter((d) => d?.name);
              return (
                <article key={a.id} data-testid="preview-article" className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  {imageUrl && (
                    <div className="relative aspect-[16/9] bg-slate-100">
                      <img src={imageUrl} alt={a.title} className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-7 sm:p-10">
                    {/* Eyebrow meta — kept quiet so it doesn't compete with the headline */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-400 mb-3">
                      {a.created_at && <span>{fmtDate(a.created_at)}</span>}
                      {!!a.word_count && <><span className="text-slate-300">·</span><span>{a.word_count} words</span></>}
                      {!!a.word_count && <><span className="text-slate-300">·</span><span>{Math.max(1, Math.round(a.word_count / 200))} min read</span></>}
                    </div>

                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight mb-4">{a.title}</h2>

                    {/* Where this article will be published */}
                    {dests.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 mb-6 pb-6 border-b border-slate-100" data-testid="preview-destinations">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18zM3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18M12 3a15 15 0 000 18" />
                          </svg>
                          Publishing to
                        </span>
                        {dests.map((d) => (
                          <span
                            key={d.name}
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                              d.tier === 'external'
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-kc-blue/10 text-kc-blue border-kc-blue/20'
                            }`}
                          >
                            {d.name}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className={`${PROSE_CLASS} cs-preview-body`} data-testid="preview-article-body"
                      dangerouslySetInnerHTML={{ __html: bodyToHtml(a.body) }} />
                  </div>
                  <RequestEdits token={token} articleId={a.id} />
                </article>
              );
            })}
          </div>
        )}

        <footer className="mt-14 pt-8 border-t border-slate-200 text-center">
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold mb-1">KeyCrew Content Studio</div>
          <p className="text-xs text-slate-400">This is a read-only preview. Final published versions may vary slightly.</p>
        </footer>
      </div>
    </div>
  );
};

export default PreviewPage;
